import is from '@sindresorhus/is';
import { z } from 'zod';

import { compareLeftSchema, compareRightSchema } from '../data-contract/position-alignment';
import { createUrlTileCodec, urlTileKeywordCodec, urlTileStringCodec } from '../url-tiles/create-url-tile-codec';
import { alignmentPageStepSchema } from './model/page-step';

export const POSITION_ALIGNMENT_TILE_TYPE = 'v1.position-alignment.alignment' as const;
export const POSITION_ALIGNMENT_TILE_SLOT_KEY = 'position-alignment.alignment';

/** One token of a tile value: tile values split on dots. */
const tileTokenSchema = z
    .string()
    .min(1)
    .refine((value) => !value.includes('.'));

export const isTileToken = (value: string): boolean => tileTokenSchema.safeParse(value).success;

export const alignmentTileSchema = z.object({
    alignmentId: z.uuid(),
    automationId: tileTokenSchema.optional(),
    isCommentsOpen: z.boolean().optional(),
    step: alignmentPageStepSchema.optional(),
    /** A comment thread a link points at, focused in the comments. */
    threadId: tileTokenSchema.optional(),
    type: z.literal(POSITION_ALIGNMENT_TILE_TYPE),
});

export type AlignmentTilePayload = z.infer<typeof alignmentTileSchema>;

export const alignmentTileCodec = createUrlTileCodec<AlignmentTilePayload>({
    options: [
        { codec: urlTileStringCodec, field: 'step', key: 'step' },
        { codec: urlTileKeywordCodec({ closed: false, open: true }), field: 'isCommentsOpen', key: 'comments' },
        { codec: urlTileStringCodec, field: 'automationId', key: 'automation' },
        { codec: urlTileStringCodec, field: 'threadId', key: 'thread' },
    ],
    segments: ['alignmentId'],
    type: POSITION_ALIGNMENT_TILE_TYPE,
});

export const isAlignmentTilePayload = (payload: unknown): payload is AlignmentTilePayload =>
    alignmentTileSchema.safeParse(payload).success;

export const createAlignmentTilePayload = ({
    alignmentId,
    automationId,
    isCommentsOpen,
    step,
    threadId,
}: Pick<
    AlignmentTilePayload,
    'alignmentId' | 'automationId' | 'isCommentsOpen' | 'step' | 'threadId'
>): AlignmentTilePayload => ({
    alignmentId,
    ...(is.undefined(automationId) ? {} : { automationId }),
    ...(is.undefined(isCommentsOpen) ? {} : { isCommentsOpen }),
    step,
    ...(is.undefined(threadId) ? {} : { threadId }),
    type: POSITION_ALIGNMENT_TILE_TYPE,
});

export const POSITION_ALIGNMENT_COMPARE_TILE_TYPE = 'v1.position-alignment.compare' as const;
export const POSITION_ALIGNMENT_COMPARE_TILE_SLOT_KEY = 'position-alignment.compare';

export const compareTileSchema = z.object({
    alignmentId: z.uuid(),
    left: compareLeftSchema,
    right: compareRightSchema,
    type: z.literal(POSITION_ALIGNMENT_COMPARE_TILE_TYPE),
});

export type CompareTilePayload = z.infer<typeof compareTileSchema>;

export const compareTileCodec = createUrlTileCodec<CompareTilePayload>({
    options: [],
    segments: ['alignmentId', 'left', 'right'],
    type: POSITION_ALIGNMENT_COMPARE_TILE_TYPE,
});

export const createCompareTilePayload = ({
    alignmentId,
    left,
    right,
}: Pick<CompareTilePayload, 'alignmentId' | 'left' | 'right'>): CompareTilePayload => ({
    alignmentId,
    left,
    right,
    type: POSITION_ALIGNMENT_COMPARE_TILE_TYPE,
});

export const POSITION_ALIGNMENT_TASK_TILE_TYPE = 'v1.position-alignment.task' as const;
export const POSITION_ALIGNMENT_NEW_TASK_TILE_TYPE = 'v1.position-alignment.new-task' as const;
export const POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY = 'position-alignment.task';

export const taskTileSchema = z.object({
    alignmentId: z.uuid(),
    taskId: tileTokenSchema,
    type: z.literal(POSITION_ALIGNMENT_TASK_TILE_TYPE),
});

export type TaskTilePayload = z.infer<typeof taskTileSchema>;

export const taskTileCodec = createUrlTileCodec<TaskTilePayload>({
    options: [],
    segments: ['alignmentId', 'taskId'],
    type: POSITION_ALIGNMENT_TASK_TILE_TYPE,
});

export const newTaskTileSchema = z.object({
    alignmentId: z.uuid(),
    type: z.literal(POSITION_ALIGNMENT_NEW_TASK_TILE_TYPE),
});

export type NewTaskTilePayload = z.infer<typeof newTaskTileSchema>;

export const newTaskTileCodec = createUrlTileCodec<NewTaskTilePayload>({
    options: [],
    segments: ['alignmentId'],
    type: POSITION_ALIGNMENT_NEW_TASK_TILE_TYPE,
});

export const createTaskTilePayload = ({
    alignmentId,
    taskId,
}: Pick<TaskTilePayload, 'alignmentId' | 'taskId'>): TaskTilePayload => ({
    alignmentId,
    taskId,
    type: POSITION_ALIGNMENT_TASK_TILE_TYPE,
});

export const createNewTaskTilePayload = (alignmentId: string): NewTaskTilePayload => ({
    alignmentId,
    type: POSITION_ALIGNMENT_NEW_TASK_TILE_TYPE,
});

export const POSITION_ALIGNMENT_AUTOMATION_TILE_TYPE = 'v1.position-alignment.automation' as const;
export const POSITION_ALIGNMENT_AUTOMATION_TILE_SLOT_KEY = 'position-alignment.automation';

/** A recommended automation of a task of the copy, read through the alignment. */
export const automationTileSchema = z.object({
    alignmentId: z.uuid(),
    automationId: tileTokenSchema,
    taskId: tileTokenSchema,
    type: z.literal(POSITION_ALIGNMENT_AUTOMATION_TILE_TYPE),
});

export type AutomationTilePayload = z.infer<typeof automationTileSchema>;

export const automationTileCodec = createUrlTileCodec<AutomationTilePayload>({
    options: [],
    segments: ['alignmentId', 'taskId', 'automationId'],
    type: POSITION_ALIGNMENT_AUTOMATION_TILE_TYPE,
});

export const createAutomationTilePayload = ({
    alignmentId,
    automationId,
    taskId,
}: Pick<AutomationTilePayload, 'alignmentId' | 'automationId' | 'taskId'>): AutomationTilePayload => ({
    alignmentId,
    automationId,
    taskId,
    type: POSITION_ALIGNMENT_AUTOMATION_TILE_TYPE,
});

/** The task whose tile is open, to mark its row. */
export const openTaskIdOf = (tiles: readonly { readonly payload: unknown }[]): string | undefined =>
    tiles.flatMap(({ payload }) => {
        const parsed = taskTileSchema.safeParse(payload);

        return parsed.success ? [parsed.data.taskId] : [];
    })[0];
